// Left navigation sidebar. Discovery layer for everything in the app —
// pages still gate themselves server-side, this just controls visibility.
//
// Three sections, filtered by role:
//   - Workspace: always visible
//   - Admin: visible if any project admin role OR god session active
//   - God: visible only when god session is active
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ComponentType,
} from "react";
import { NavLink } from "react-router-dom";
import {
  FolderKanban,
  History as HistoryIcon,
  Home,
  KeyRound,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  PlayCircle,
  Sparkles,
  Server,
  Unlock,
  Users,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { APP_VERSION, GIT_SHA } from "@/lib/version";

type SidebarCtx = {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  mobileOpen: boolean;
  setMobileOpen: (v: boolean) => void;
};

const SidebarContext = createContext<SidebarCtx | undefined>(undefined);
const STORAGE_KEY = "jg.sidebar.collapsed";

export function SidebarProvider({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsedState] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });
  const [mobileOpen, setMobileOpen] = useState(false);

  function setCollapsed(v: boolean) {
    setCollapsedState(v);
    try {
      window.localStorage.setItem(STORAGE_KEY, v ? "1" : "0");
    } catch {
      /* private mode / disabled storage — ignore */
    }
  }

  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <SidebarContext.Provider
      value={{ collapsed, setCollapsed, mobileOpen, setMobileOpen }}
    >
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar() {
  const ctx = useContext(SidebarContext);
  if (!ctx) throw new Error("useSidebar must be used within SidebarProvider");
  return ctx;
}

type NavItem = {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  end?: boolean;
};
type NavSection = { heading: string; items: NavItem[]; visible: boolean };

function useNavSections(): NavSection[] {
  const { me } = useAuth();
  const showGod = Boolean(me?.god.active);
  const showAdmin = showGod || (me?.roles.some((r) => r.role === "admin") ?? false);

  return [
    {
      heading: "Workspace",
      visible: true,
      items: [
        { to: "/", label: "Home", icon: Home, end: true },
        { to: "/demo", label: "Demo Chat", icon: Sparkles },
        { to: "/playground", label: "Playground", icon: PlayCircle },
        { to: "/history", label: "History", icon: HistoryIcon },
      ],
    },
    {
      heading: "Admin",
      visible: showAdmin,
      items: [
        { to: "/admin/projects", label: "Projects", icon: FolderKanban },
        { to: "/admin/users", label: "Users", icon: Users },
        { to: "/admin/runners", label: "Runners", icon: Server },
        { to: "/admin/api-keys", label: "API Keys", icon: KeyRound },
      ],
    },
    {
      heading: "God",
      visible: showGod,
      items: [{ to: "/god", label: "God Dashboard", icon: Unlock }],
    },
  ];
}

function NavItems({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const sections = useNavSections().filter((s) => s.visible);

  return (
    <nav className="flex flex-1 flex-col gap-4 overflow-y-auto px-2 py-3">
      {sections.map((section) => (
        <div key={section.heading}>
          {!collapsed && (
            <div className="mb-1 px-2 text-xs font-medium uppercase tracking-wide text-muted-foreground/70">
              {section.heading}
            </div>
          )}
          <ul className="space-y-0.5">
            {section.items.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  onClick={onNavigate}
                  title={collapsed ? item.label : undefined}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2 rounded px-2 py-1.5 text-sm transition-colors",
                      collapsed && "justify-center",
                      isActive
                        ? "bg-teal-500/15 font-medium text-teal-700 dark:text-teal-300"
                        : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
                    )
                  }
                >
                  <item.icon className="h-4 w-4 shrink-0" />
                  {!collapsed && <span className="truncate">{item.label}</span>}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function SidebarFooter({ collapsed }: { collapsed: boolean }) {
  const { me } = useAuth();
  const email = me?.user.email ?? "";
  const versionLine = `v${APP_VERSION} · ${GIT_SHA}`;

  if (collapsed) {
    return (
      <div className="border-t px-1 py-2 text-center">
        <div
          className="truncate font-mono text-[10px] text-muted-foreground"
          title={versionLine}
        >
          {GIT_SHA}
        </div>
      </div>
    );
  }

  return (
    <div className="border-t px-3 py-2 text-xs text-muted-foreground">
      {email && (
        <div className="truncate" title={email}>
          {email}
        </div>
      )}
      <div className="mt-0.5 font-mono text-[10px]" title={versionLine}>
        {versionLine}
      </div>
    </div>
  );
}

export function Sidebar() {
  const { collapsed, setCollapsed, mobileOpen, setMobileOpen } = useSidebar();

  return (
    <>
      <aside
        className={cn(
          "sticky top-14 hidden h-[calc(100vh-3.5rem)] shrink-0 flex-col border-r bg-background md:flex",
          collapsed ? "w-12" : "w-56"
        )}
      >
        <NavItems collapsed={collapsed} />
        <SidebarFooter collapsed={collapsed} />
        <div className="border-t p-2">
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "w-full",
              collapsed ? "justify-center" : "justify-start"
            )}
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <>
                <PanelLeftClose className="mr-2 h-4 w-4" />
                <span>Collapse</span>
              </>
            )}
          </Button>
        </div>
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileOpen(false)}
            aria-hidden
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r bg-background shadow-lg">
            <div className="flex h-14 items-center justify-between border-b px-4">
              <span className="font-semibold">Menu</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
            <NavItems
              collapsed={false}
              onNavigate={() => setMobileOpen(false)}
            />
            <SidebarFooter collapsed={false} />
          </aside>
        </div>
      )}
    </>
  );
}

export function SidebarMobileToggle() {
  const { setMobileOpen } = useSidebar();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="md:hidden"
      onClick={() => setMobileOpen(true)}
      aria-label="Open menu"
    >
      <Menu className="h-4 w-4" />
    </Button>
  );
}
